import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { DataTable } from '@/components/data/DataTable'
import { JobFormDialog } from '../components/JobFormDialog'
import { JobDeleteDialog } from '../components/JobDeleteDialog'
import { JobViewDialog } from '../components/JobViewDialog'
import { useJobs, useDeleteJob, useRunJob, usePauseJob, useResumeJob } from '../hooks'
import { createJobColumns } from '../columns'
import type { Job } from '@/types/entities'

export function JobListPage() {
    const { t } = useTranslation()
    const [selectedJob, setSelectedJob] = useState<Job | null>(null)
    const [showCreateDialog, setShowCreateDialog] = useState(false)
    const [showEditDialog, setShowEditDialog] = useState(false)
    const [showDeleteDialog, setShowDeleteDialog] = useState(false)
    const [showViewDialog, setShowViewDialog] = useState(false)

    const { data: jobsResponse, isLoading } = useJobs()
    const deleteJob = useDeleteJob()
    const runJob = useRunJob()
    const pauseJob = usePauseJob()
    const resumeJob = useResumeJob()

    const jobs = jobsResponse?.data || []

    const handleEdit = (job: Job) => {
        setSelectedJob(job)
        setShowEditDialog(true)
    }

    const handleDelete = (job: Job) => {
        setSelectedJob(job)
        setShowDeleteDialog(true)
    }

    const handleView = (job: Job) => {
        setSelectedJob(job)
        setShowViewDialog(true)
    }

    const handleRun = (job: Job) => {
        runJob.mutate(job.id)
    }

    const handlePause = (job: Job) => {
        pauseJob.mutate(job.id)
    }

    const handleResume = (job: Job) => {
        resumeJob.mutate(job.id)
    }

    const handleDeleteConfirm = () => {
        if (selectedJob) {
            deleteJob.mutate(selectedJob.id, {
                onSuccess: () => {
                    setShowDeleteDialog(false)
                    setSelectedJob(null)
                },
            })
        }
    }

    const handleExport = () => {
        // TODO: Implement CSV export
        console.log('Export jobs to CSV')
    }

    const columns = createJobColumns(handleEdit, handleDelete, handleView, handleRun, handlePause, handleResume, t)

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-muted-foreground">Loading jobs...</div>
            </div>
        )
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold">Jobs</h1>
                    <p className="text-muted-foreground">
                        Manage your backup jobs and schedules
                    </p>
                </div>
                <Button onClick={() => setShowCreateDialog(true)}>
                    <Plus className="mr-2 h-4 w-4" />
                    Create Job
                </Button>
            </div>

            {/* Table */}
            <DataTable
                columns={columns}
                data={jobs}
                searchKey="name"
                searchPlaceholder="Search jobs..."
                onExport={handleExport}
            />

            {/* Dialogs */}
            <JobFormDialog
                open={showCreateDialog}
                onOpenChange={setShowCreateDialog}
                job={null}
            />

            <JobFormDialog
                open={showEditDialog}
                onOpenChange={setShowEditDialog}
                job={selectedJob}
            />

            <JobDeleteDialog
                open={showDeleteDialog}
                onOpenChange={setShowDeleteDialog}
                job={selectedJob}
                onConfirm={handleDeleteConfirm}
                isLoading={deleteJob.isPending}
            />

            <JobViewDialog
                open={showViewDialog}
                onOpenChange={setShowViewDialog}
                job={selectedJob}
            />
        </div>
    )
}



